import { motion } from 'framer-motion';
import React from 'react';
import {
    Box,
    Container,
    Grid,
    Typography,
    Paper,
    useTheme,
    alpha,
    Chip
} from '@mui/material';
import {
    AccountBalance as CooperativeIcon,
    Calculate as AccountingIcon,
    School as SchoolIcon,
    WaterDrop as WaterIcon,
    TrendingUp as PerformanceIcon,
    RocketLaunch as RocketIcon
} from '@mui/icons-material';

const About = () => {
    const theme = useTheme();

    const services = [
        {
            icon: <CooperativeIcon sx={{ fontSize: 40, color: 'white' }} />,
            title: "Easy Cooperative Software",
            description: "Financial Solution To Advance Your Cooperative."
        },
        {
            icon: <AccountingIcon sx={{ fontSize: 40, color: 'white' }} />,
            title: "Easy Accounting Software",
            description: "Calculating The Best Opportunities For You."
        },
        {
            icon: <SchoolIcon sx={{ fontSize: 40, color: 'white' }} />,
            title: "Easy School & College Software",
            description: "For Better Management Of School & College."
        },
        {
            icon: <WaterIcon sx={{ fontSize: 40, color: 'white' }} />,
            title: "Easy Khanepani Software",
            description: "Connecting Khanepani With IT."
        }
    ];

    // Motion variants
    const fadeUp = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
    };

    const cardVariant = {
        hidden: { opacity: 0, y: 50, scale: 0.9 },
        visible: i => ({
            opacity: 1,
            y: 0,
            scale: 1,
            transition: { delay: i * 0.2, duration: 0.6 }
        })
    };

    return (
        <Box
            id="about"
            sx={{
                backgroundColor: alpha(theme.palette.primary.light, 0.05),
                py: { xs: 6, md: 10 },
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '-10%',
                    right: '-10%',
                    width: '300px',
                    height: '300px',
                    borderRadius: '50%',
                    background: `radial-gradient(${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
                    zIndex: 0
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-10%',
                    left: '-10%',
                    width: '250px',
                    height: '250px',
                    borderRadius: '50%',
                    background: `radial-gradient(${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
                    zIndex: 0
                }
            }}
        >
            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                <Grid container spacing={4} alignItems="center">
                    {/* Left Content */}
                    <Grid item xs={12} md={5}>
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={fadeUp}
                        >
                            <Box sx={{ position: 'relative' }}>
                                <Chip
                                    icon={<RocketIcon />}
                                    label="About Our Company"
                                    sx={{
                                        mb: 3,
                                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                        color: theme.palette.primary.dark,
                                        fontWeight: 'bold',
                                        '& .MuiChip-icon': {
                                            color: theme.palette.primary.main
                                        }
                                    }}
                                />
                                <Typography
                                    variant="h3"
                                    component="h2"
                                    gutterBottom
                                    sx={{
                                        fontWeight: 'bold',
                                        mb: 3,
                                        color: theme.palette.primary.dark,
                                        fontSize: { xs: '2rem', md: '2.5rem' },
                                        position: 'relative',
                                        '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            bottom: -10,
                                            left: 0,
                                            width: '60px',
                                            height: '4px',
                                            borderRadius: 2,
                                            background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary.light})`
                                        }
                                    }}
                                >
                                    <Box
                                        component="span"
                                        sx={{
                                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                            backgroundClip: 'text',
                                            WebkitBackgroundClip: 'text',
                                            color: 'red',
                                        }}
                                    >
                                        Easy
                                    </Box>{' '}
                                    Software for Better Performance
                                </Typography>

                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        fontSize: '1.1rem',
                                        lineHeight: 1.6,
                                        mb: 3
                                    }}
                                >
                                    Easy Software Pvt ltd aims to provide creative and easy software for
                                    companies and other association. We believe in becoming a leading
                                    organization by providing the best software.
                                </Typography>

                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mt: 4,
                                    p: 2,
                                    borderRadius: 2,
                                    background: `linear-gradient(to right, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.light, 0.05)})`,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                                }}>
                                    <Box
                                        sx={{
                                            mr: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 50,
                                            height: 50,
                                            borderRadius: '50%',
                                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                            boxShadow: `0 4px 10px ${alpha(theme.palette.primary.main, 0.3)}`
                                        }}
                                    >
                                        <PerformanceIcon sx={{ fontSize: 30, color: 'white' }} />
                                    </Box>
                                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: theme.palette.text.secondary }}>
                                        Driving digital transformation with innovative solutions
                                    </Typography>
                                </Box>
                            </Box>
                        </motion.div>
                    </Grid>

                    {/* Right Content - Services Grid */}
                    <Grid item xs={12} md={7}>
                        <Grid container spacing={3}>
                            {services.map((service, index) => (
                                <Grid item xs={12} sm={6} key={index}>
                                    <motion.div
                                        custom={index}
                                        initial="hidden"
                                        whileInView="visible"
                                        viewport={{ once: true }}
                                        variants={cardVariant}
                                    >
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 3,
                                                height: 250,
                                                background: `linear-gradient(to bottom right, white, ${alpha(theme.palette.primary.light, 0.1)})`,
                                                borderRadius: 3,
                                                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                                transition: 'all 0.3s ease-in-out',
                                                position: 'relative',
                                                overflow: 'hidden',
                                                '&:hover': {
                                                    transform: 'translateY(-8px)',
                                                    boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                                                    borderColor: alpha(theme.palette.primary.main, 0.3),
                                                    '&::before': {
                                                        transform: 'scale(1)'
                                                    }
                                                },
                                                '&::before': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    height: '4px',
                                                    background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                                                    transform: 'scaleX(0)',
                                                    transition: 'transform 0.3s ease-in-out'
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        width: 70,
                                                        height: 70,
                                                        borderRadius: '50%',
                                                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                                        boxShadow: `0 4px 15px ${alpha(theme.palette.primary.main, 0.3)}`,
                                                        mb: 2
                                                    }}
                                                >
                                                    {service.icon}
                                                </Box>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        mt: 1,
                                                        mb: 1,
                                                        color: theme.palette.primary.dark
                                                    }}
                                                >
                                                    {service.title}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: theme.palette.text.secondary,
                                                        lineHeight: 1.5
                                                    }}
                                                >
                                                    {service.description}
                                                </Typography>
                                            </Box>
                                        </Paper>
                                    </motion.div>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default About;
